// Cliente do cartão de controle: sessão continua no cookie HttpOnly e o QR é só
// um identificador opaco. Não há fila nem confirmação de escrita offline aqui.
import type {
  CartaoControleEdicaoView,
  CartaoControleStatus,
  PrepararCartaoControleInput,
  ReimprimirCartaoControleInput,
  ResumoLoteQrView,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';

const base = '/api/v1/producao/cartoes-controle';
// Mesmo prefixo de API do apiFetch (config pública, vazio = mesma origem).
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/**
 * Falha da leitura do resumo QR com o status HTTP preservado (apiFetch não o expõe).
 * A mensagem nunca carrega o corpo da resposta: em 401/403/404 o servidor não pode
 * vazar metadados do lote por esta via. `status` é null quando não houve resposta (rede).
 */
export class ErroResumoQr extends Error {
  readonly status: number | null;
  constructor(status: number | null, mensagem: string) {
    super(mensagem);
    this.name = 'ErroResumoQr';
    this.status = status;
  }
}

async function lerResumoQr(token: string): Promise<ResumoLoteQrView> {
  let resposta: Response;
  try {
    resposta = await fetch(`${API_BASE_URL}${base}/qr/${encodeURIComponent(token)}`, { method: 'GET', credentials: 'include' });
  } catch {
    throw new ErroResumoQr(null, 'Não foi possível carregar os dados. Verifique a conexão e tente novamente.');
  }
  if (!resposta.ok) throw new ErroResumoQr(resposta.status, `Erro ${resposta.status} ao consultar o resumo.`);
  try {
    return (await resposta.json()) as ResumoLoteQrView;
  } catch {
    throw new ErroResumoQr(resposta.status, 'Resposta do resumo inválida.');
  }
}

export function novaChaveIdempotencia(): string {
  return globalThis.crypto?.randomUUID?.() ?? `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Falha de escrita do CP com o status HTTP preservado (409, 4xx definitivo ou 5xx incerto). */
export class ErroCartaoControle extends Error {
  readonly status: number;
  constructor(status: number, mensagem: string) {
    super(mensagem);
    this.name = 'ErroCartaoControle';
    this.status = status;
  }
}

async function corpo<T>(resposta: Response): Promise<T> {
  if (!resposta.ok) throw new ErroCartaoControle(resposta.status, (await resposta.text()) || `Erro ${resposta.status}.`);
  return resposta.json() as Promise<T>;
}

export const cartaoControleApi = {
  status: (loteId: string): Promise<CartaoControleStatus> =>
    apiFetch<CartaoControleStatus>(`${base}/lotes/${encodeURIComponent(loteId)}/status`),
  resumoQr: (token: string): Promise<ResumoLoteQrView> => lerResumoQr(token),
  async preparar(loteId: string, opId: string): Promise<CartaoControleEdicaoView> {
    const resposta = await apiMutate(`${base}/preparar`, {
      method: 'POST', body: { loteId, opId, chaveIdempotencia: novaChaveIdempotencia() } satisfies PrepararCartaoControleInput,
    });
    return corpo<CartaoControleEdicaoView>(resposta);
  },
  /** A chave pertence à tentativa: quem chama a reutiliza após resultado incerto (T-16-12-T). */
  async reimprimir(edicaoId: string, motivo: string, chaveIdempotencia: string = novaChaveIdempotencia()): Promise<CartaoControleEdicaoView> {
    const resposta = await apiMutate(`${base}/${encodeURIComponent(edicaoId)}/reimprimir`, {
      method: 'POST', body: { motivo, chaveIdempotencia } satisfies ReimprimirCartaoControleInput,
    });
    return corpo<CartaoControleEdicaoView>(resposta);
  },
  async registrarAberturaFluxoImpressao(edicaoId: string): Promise<void> {
    const resposta = await apiMutate(`${base}/${encodeURIComponent(edicaoId)}/fluxo-impressao`, { method: 'POST' });
    if (!resposta.ok) throw new Error((await resposta.text()) || 'Não foi possível preparar a impressão.');
  },
};
